<template>
  <div v-if="isShown" class="sf-banner" :class="outerContainer">
    <div class="sf-banner-left">
      <span class="sf-banner-icon fa" :class="effectiveIconClass" />
      <span class="sf-banner-text">
        <span v-if="title" class="sf-banner-title">{{ title }}</span>
        <span class="sf-banner-description"
          ><slot><span v-dompurify-html="descriptionHtml"></span></slot
        ></span>
        <span v-if="hasLink" class="sf-banner-link"
          ><slot name="link"><span v-dompurify-html="linkHtml"></span></slot
        ></span>
      </span>
    </div>
    <div class="sf-banner-right">
      <div v-if="hasActions" class="sf-banner-actions">
        <slot name="actions">
          <span v-dompurify-html="actionsHtml"></span>
        </slot>
      </div>
      <span
        v-if="showClose"
        class="sf-banner-close fa fa-close"
        data-testid="sf-banner-close"
        @click="close"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import type { BannerVariant } from '@app/shared/types/banner'
import { bannerVariants } from '@app/shared/types/banner'

import type { BannerIconType, bannerLevelType } from './constants'
import { icons, levels } from './constants'
import { computed, ref, useSlots, onBeforeUnmount } from 'vue'

defineOptions({
  name: 'SfBannerBase',
})

const slots = useSlots()

interface Props {
  autoCloseAfter?: number
  hasLongText?: boolean
  iconClass?: BannerIconType
  isFullWidth?: boolean
  level: bannerLevelType
  selfManaged?: boolean
  showClose?: boolean
  title?: string
  variant: BannerVariant
  descriptionHtml?: string
  actionsHtml?: string
  linkHtml?: string
}

const props = withDefaults(defineProps<Props>(), {
  autoCloseAfter: undefined,
  hasLongText: false,
  iconClass: '',
  isFullWidth: false,
  level: levels.system,
  title: '',
  variant: bannerVariants.info,
  descriptionHtml: '',
  actionsHtml: '',
  linkHtml: '',
})

const isShown = ref(true)
let timeoutToClear: ReturnType<typeof setTimeout>

if (props.autoCloseAfter) {
  timeoutToClear = setTimeout(() => {
    close()
  }, props.autoCloseAfter)
}

onBeforeUnmount(() => {
  if (timeoutToClear) {
    clearTimeout(timeoutToClear)
  }
})

const effectiveIconClass = computed(() => {
  if (props.iconClass) {
    return props.iconClass
  }

  return icons[props.variant]
})

const hasActions = computed(() => {
  return !!slots.actions || !!props.actionsHtml
})

const hasLink = computed(() => {
  return !!slots.link || !!props.linkHtml
})

const outerContainer = computed(() => {
  const effectiveClass = `sf-banner-${props.level}-${props.variant}`
  return {
    [effectiveClass]: true,
    'sf-banner-long-text': props.hasLongText,
    [`${effectiveClass}--full-width`]: props.isFullWidth,
  }
})

const emit = defineEmits(['close'])

const close = (): void => {
  if (props.selfManaged || props.autoCloseAfter) {
    isShown.value = false
  }

  emit('close')
}
</script>
